import Step from '@mui/material/Step';
import classNames from 'classnames';
import firebase from 'firebase/app';
import 'firebase/auth';
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Tab, Tabs } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';

import crud from '../../../../api/crud';
import useNavigateWithSearch from '../../../../hooks/useNavigateWithSearch';
import usePayOnShipConfig from '../../../../hooks/usePayOnShipConfig';
import { useFirebaseAuth } from '../../../global/FirebaseProvider/FirebaseProvider';
import { FORDAYS_STORE_ID } from '../../config/config';
import { CONTACT_US_URL } from '../../config/externalURL';
import {
  REGISTER_CODE_PARAM,
  REGISTER_FROM_CHECKOUT_PARAM,
  REGISTER_PIN_PARAM,
  ROUTE_TAKE_BACK_BAGS_DETAILS,
} from '../../config/routes';
import LoadingBar from '../../global/LoadingBar';
import MessageContent from '../../global/MessageContent';
import StepLabel from '../../global/Mui/StepLabel';
import Stepper from '../../global/Mui/Stepper';
import TbbRewardPage from '../../global/RegisterTbb/RewardCouponScreen/TbbRewardPage';
import IconCloudy from '../../images/icon-cloudy.svg';
import { isExpiredLabel, qrIsExpired, shippingProviderStatus } from '../../utils/easyPost';
import { errorCodes } from '../../utils/errors';
import { getSearchParam } from '../../utils/routes';
import { getPaymentStatus, getRedemption, getStore, postRedemptionCode, postShippingLabelRegenerate } from '../../utils/service';
import { pushBagRegisteredEvent } from '../../utils/gtm';
import {
  DEFAULT_STEP,
  REDEMPTION_CODE_STATUS_IDS,
  STEPS,
  STEPS_PAY_ON_SHIP,
  TABS,
  getHeaderLogo,
  getStepButtonTrackerProps,
  isTechRecyclingProductType,
  isVirtualBoxRecyclingProductType,
  STEPS_VIRTUAL_BOX,
} from '../../utils/tbbRegistration';
import GeneratingShippingLabel from './Components/Animations/GeneratingShippingLabel';
import ProcessingPayment from './Components/Animations/ProcessingPayment';
import {
  BatteryAlert,
  DeviceBatteryWarning,
  EmailForm,
  EnterNewAddressView,
  Invalid,
  Login,
  Payment,
  Pin,
  PostRegistration,
  PostRegistrationVirtualBox,
  QrCodeForm,
  QrView,
  RecyclingList,
  ReuseBox,
  SelectAddressView,
  SelectShippingProviderView,
  Service,
  ShippingAndReward,
  ShippingDetails,
  ShippingLabelGenerationError,
  StickersAdvice,
  Welcome,
  WelcomeVirtualBox,
} from './Steps';
import WelcomePayOnShip from './Steps/WelcomePayOnShip';

import './Register.scss';
import { getUSStates, US_COUNTRY_CODE, US_COUNTRY_NAME } from '../../utils/locations';

const CODE_LENGTH = 13;
const ERROR_PATTERN = /[\r\n\s]+/g;

const TABS_PROPS = {
  title: false,
  mountOnEnter: true,
  unmountOnExit: true,
};

const MESSAGE_DEFAULT = {
  image: undefined,
  title: undefined,
  cta: {
    text: undefined,
    onClick: undefined,
  },
};

const RedemptionLandingPage = () => {
  const {
    user,
    userData,
    isSignedIn,
    isLoading: isLoadingFirebase,
    reloadUserData,
  } = useFirebaseAuth();
  const navigateWithSearch = useNavigateWithSearch();
  const { search, pathname } = useLocation();
  const [redemptionCode, setRedemptionCode] = useState(null);
  const [isValidCode, setIsValidCode] = useState(null);
  const [emailInput, setEmailInput] = useState('');
  const [requirePin, setRequirePin] = useState(true);
  const [recyclingProductType, setRecyclingProductType] = useState(null);
  const [pinValues, setPinValues] = useState(Array(6).fill(''));
  const [step, setStep] = useState(DEFAULT_STEP);
  const [tab, setTab] = useState(TABS.WELCOME);
  const [qrCodeImgUrl, setQrCodeImgUrl] = useState('');
  const [printLabelUrl, setPrintLabelUrl] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [couponData, setCouponData] = useState({});
  const [copied, setCopied] = useState(false);
  const [fromAddress, setFromAddress] = useState({});
  const [shippingMethod, setShippingMethod] = useState({});
  const [storeConfig, setStoreConfig] = useState({});
  const [creditAmt, setCreditAmt] = useState(0);
  const [isNewUser, setIsNewUser] = useState(false);
  const [isAQrExpired, setIsAQrExpired] = useState(false);
  const [expirationLabelDate, setExpirationLabelDate] = useState(null);
  const [messageContent, setMessageContent] = useState(MESSAGE_DEFAULT);
  const [selectedShippingMethodIndex, setSelectedShippingMethodIndex] = useState(0);
  const [promoCreditAmount, setPromoCreditAmount] = useState(0);
  const [showRewardDialog, setShowRewardDialog] = useState(true);
  const [payOnShipment, setPayOnShipment] = useState({
    isPayOnShip: false,
    payOnShipFee: 0,
    isPaid: false,
  });
  const [errorMessage, setErrorMessage] = useState('');
  const paymentStatusInFlightRef = useRef(false);
  const [showingAnimation, setShowingAnimation] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const {
    isLoading: isLoadingPayOnShipConfig, payOnShipConfig,
  } = usePayOnShipConfig({ enabled: payOnShipment.isPayOnShip });

  const query = new URLSearchParams(search);

  const isTechRecycling = isTechRecyclingProductType(recyclingProductType);
  const isVirtualBox = isVirtualBoxRecyclingProductType(recyclingProductType);

  let steps;
  if (payOnShipment.isPayOnShip) {
    steps = STEPS_PAY_ON_SHIP;
  } else if (isVirtualBox) {
    steps = STEPS_VIRTUAL_BOX;
  } else {
    steps = STEPS;
  }

  const handleTabChange = useCallback(async (newTab) => {
    if (newTab === TABS.SHIPPING_REWARD) {
      try {
        await reloadUserData();
      } catch (e) {
        // user data reload failure should not block navigation
      }
    }

    setTab(newTab);
  }, [reloadUserData]);

  const handleContactSupport = useCallback(() => {
    window.open(CONTACT_US_URL, '_blank');
  }, []);

  const handleGenericError = useCallback((title) => {
    setMessageContent({
      image: IconCloudy,
      title: title ?? 'WE’VE ENCOUNTERED DIFFICULTIES WHILE REGISTERING THE BAG',
      cta: {
        text: 'CONTACT SUPPORT',
        onClick: handleContactSupport,
      },
    });
    handleTabChange(TABS.MESSAGE);
  }, []);

  const validatePaymentStatus = useCallback(async (fromCheckout = false) => {
    const {
      data,
      error: getPaymentStatusError,
    } = await getPaymentStatus(redemptionCode, fromCheckout);

    if (!getPaymentStatusError && data?.isPaid) {
      setPayOnShipment(data);
      handleTabChange(TABS.PIN);
      return;
    }

    // Fallback for subscription payments returning from checkout:
    // if user has an active subscription, treat as paid and proceed to PIN.
    if (fromCheckout && userData?.activeSubscription) {
      setPayOnShipment((prev) => ({ ...prev, isPaid: true }));
      handleTabChange(TABS.PIN);
      return;
    }

    // For checkout returns where the subscription is not yet reflected,
    // offer a reload instead of a generic error.
    if (fromCheckout) {
      setMessageContent({
        image: IconCloudy,
        title: 'WE\'RE HAVING TROUBLE REGISTERING THE BAG AFTER CHECKOUT',
        cta: {
          text: 'TRY AGAIN',
          onClick: () => window.location.reload(),
        },
      });
      handleTabChange(TABS.MESSAGE);
      return;
    }

    handleGenericError(errorCodes[getPaymentStatusError?.code]);
  }, [redemptionCode, handleTabChange, handleGenericError, userData?.activeSubscription]);

  const validateCodeAssignedToUser = useCallback(async () => {
    try {
      const { isCodeAssignedToUser } = await crud.get({
        path: `/redemptions/validateAssignedUser/${redemptionCode}`,
        cacheControl: 'no-cache',
      });
      if (!isCodeAssignedToUser) {
        setErrorMessage('The box you are trying to register is not assigned to your user.');
        setIsLoading(false);
        handleTabChange(TABS.INVALID);
        return false;
      }
      setIsLoading(false);
      return true;
    } catch (error) {
      setErrorMessage('An error occurred while validating the box assignment. Please try again or contact support.');
      setIsLoading(false);
      handleTabChange(TABS.INVALID);
      return false;
    }
  }, [redemptionCode, handleTabChange, setIsLoading]);

  const handlePayOnShipment = useCallback(async ({
    isPayOnShip,
    isPaid,
  }) => {
    if (!isPayOnShip) {
      return;
    }

    if (tab === TABS.LOGIN) {
      if (!isSignedIn) {
        setIsLoading(false);
        return;
      }

      if (userData?.activeSubscription) {
        handleTabChange(TABS.PIN);
        return;
      }

      if (isPaid) {
        await validatePaymentStatus();
      } else {
        handleTabChange(TABS.PAYMENT);
      }
    }

    if (tab === TABS.PIN) {
      handleTabChange(TABS.SELECT_ADDRESS);
    }

    if (tab === TABS.SERVICE) {
      if (userData?.activeSubscription) {
        handleTabChange(TABS.PIN);
      } else {
        handleTabChange(isSignedIn ? TABS.PAYMENT : TABS.LOGIN);
      }
    }

    if (tab === TABS.QR_CODE_FORM) {
      handleTabChange(TABS.SERVICE);
    }

    const searchParams = new URLSearchParams(search);
    const fromCheckout = getSearchParam(searchParams, REGISTER_FROM_CHECKOUT_PARAM) === 'true';

    if (tab === TABS.WELCOME && isSignedIn && (isPaid || fromCheckout)) {
      paymentStatusInFlightRef.current = true;
      await validatePaymentStatus(fromCheckout);
      paymentStatusInFlightRef.current = false;
      setIsLoading(false);
      return;
    }

    if (tab === TABS.WELCOME && fromCheckout && !isSignedIn) {
      // Do not clear loading yet — the fromCheckout useEffect handles it once auth is ready.
      // But set a timeout fallback so the user is never stuck indefinitely.
      return;
    }

    setIsLoading(false);
  }, [
    isSignedIn,
    user,
    userData,
    tab,
    redemptionCode,
    search,
    validatePaymentStatus,
    handleTabChange,
  ]);

  const getAndSetStore = useCallback(async (storeId) => {
    const {
      data: whiteLabelStore,
      error: getStoreError,
    } = await getStore(storeId);

    if (getStoreError) {
      return;
    }

    const whiteLabelStoreObj = {
      ...whiteLabelStore.meta,
      storeType: whiteLabelStore.storeType,
      id: whiteLabelStore.id,
      uid: whiteLabelStore.uid,
      couponRule: whiteLabelStore.couponRule,
    };

    document.body.style.backgroundColor = whiteLabelStoreObj.color2;

    if (!whiteLabelStoreObj.registerTbbFlow) {
      setIsValidCode(false);
    }

    setStoreConfig(whiteLabelStoreObj);
    setCreditAmt(whiteLabelStoreObj?.registerTbbFlow?.rewardStep?.cashValue);
  }, []);

  const getExistingRedemption = useCallback(async () => {
    const { data: redemptionData, error: redemptionError } = await getRedemption(redemptionCode);
    if (redemptionError || !redemptionData) {
      throw new Error('Failed to fetch redemption data');
    }
    let fetchedExistingRedemption = redemptionData;
    const isQrExpired = qrIsExpired(
      fetchedExistingRedemption.redeemedAt,
      fetchedExistingRedemption.shipment.shipmentStatus,
      fetchedExistingRedemption.shipment.shippingLabelExpiresAt,
    );
    setIsAQrExpired(isQrExpired);

    if (isExpiredLabel(fetchedExistingRedemption.shipment.shippingLabelExpiresAt) &&
      (fetchedExistingRedemption.shipment.shipmentStatus === shippingProviderStatus.unknown ||
      fetchedExistingRedemption.shipment.shipmentStatus === shippingProviderStatus.pre_transit ||
      fetchedExistingRedemption.shipment.shipmentStatus === shippingProviderStatus.approved)
    ) {
      try {
        const { data } = await postShippingLabelRegenerate(redemptionCode);
        if (!_.isEmpty(data)) {
          fetchedExistingRedemption = { ...fetchedExistingRedemption, ...data };
        }
      } catch (error) {
        // do nothing
      }
    }

    setCreditAmt(fetchedExistingRedemption.creditAmt);

    if (
      fetchedExistingRedemption?.labeldata
      && !JSON.parse(fetchedExistingRedemption.labeldata).error
    ) {
      const labelData = JSON.parse(fetchedExistingRedemption.labeldata);
      const qrUrl = labelData?.forms?.find(({ form_type: formType }) => formType === 'label_qr_code')?.form_url || fetchedExistingRedemption?.shipment?.qrLabelUrl;
      const printLabelUrlTmp = labelData?.postage_label?.label_pdf_url ?
        labelData.postage_label.label_pdf_url :
        labelData?.postage_label?.label_url;
      setExpirationLabelDate(fetchedExistingRedemption?.shipment.shippingLabelExpiresAt);
      setCouponData({
        couponCode: fetchedExistingRedemption?.couponData,
        summary: redemptionCode.includes('-') ? redemptionCode.split('-')[1] : '',
      });
      setQrCodeImgUrl(qrUrl);
      setPrintLabelUrl(printLabelUrlTmp);
      setShippingMethod(fetchedExistingRedemption?.shipment?.shippingMethod);
      setEmailInput(fetchedExistingRedemption?.redeemedByEmail ?? '');
      setIsNewUser(fetchedExistingRedemption?.redeemedByNew ?? false);
      const address = labelData?.from_address;
      if (address) {
        // province_code and zip_code are present for happy returns
        // state and zip are present for ups shipping label
        const provinceCode = address.state || address.province_code;
        const province = getUSStates().find(({ code }) => code === provinceCode)?.name;
        const zip = address.zip || address.zip_code;
        setFromAddress({
          ...address,
          provinceCode,
          zip,
          countryCode: US_COUNTRY_CODE,
          country: US_COUNTRY_NAME,
          province,
        });
      }
      handleTabChange(TABS.SHIPPING_REWARD);
    } else if (
      fetchedExistingRedemption?.labeldata
      && JSON.parse(fetchedExistingRedemption.labeldata).error
    ) {
      crud.post({
        path: `/redemptions/revertcode/${fetchedExistingRedemption.uid}`,
      });
    }
  }, [redemptionCode]);

  const handlePinStep = useCallback(async () => {
    if (payOnShipment.isPayOnShip) {
      handlePayOnShipment(payOnShipment);
      return;
    }

    if (storeConfig?.registerTbbFlow?.isAuthFreeFlow) {
      handleTabChange(TABS.EMAIL_FORM);
      return;
    }
    if (isSignedIn && user) {
      if (isTechRecycling) {
        handleTabChange(TABS.RECYCLING_LIST);
      } else if (isVirtualBox) {
        const isValid = await validateCodeAssignedToUser();
        if (!isValid) {
          return;
        }
        handleTabChange(TABS.REUSE_BOX);
      } else {
        handleTabChange(TABS.SELECT_ADDRESS);
      }
      return;
    }

    handleTabChange(TABS.LOGIN);
  }, [
    isSignedIn,
    user,
    handleTabChange,
    storeConfig,
    isTechRecycling,
    isVirtualBox,
    payOnShipment.isPayOnShip,
    handlePayOnShipment,
    validateCodeAssignedToUser,
  ]);

  const handleServiceStep = useCallback(() => {
    handlePayOnShipment(payOnShipment);
  }, [handlePayOnShipment, payOnShipment.isPayOnShip, payOnShipment.isPaid]);

  const returnToAddressStep = useCallback(() => {
    handleTabChange(TABS.SELECT_ADDRESS);
  }, [handleTabChange]);

  /**
   * This method is necessary because some QR codes have a printing error that needs to be removed.
   * It cleans the provided code by removing any unwanted characters
   * and truncating it to the specified length.
   * The cleaned code is then set as a query parameter in the URL.
   *
   * @param {string} code - The code to be cleaned.
   * @param {number} length - The desired length of the cleaned code. Default is CODE_LENGTH.
   * @returns {string} - The cleaned code.
   */
  const cleanCode = useCallback(
    (code, length = CODE_LENGTH) => {
      if (!code) return '';
      const cleanedCode = code.replace(ERROR_PATTERN, '').slice(0, length);
      navigateWithSearch(pathname, { [REGISTER_CODE_PARAM]: cleanedCode }, true);

      return cleanedCode;
    },
    [pathname, navigateWithSearch],
  );

  const validateCode = useCallback(async (manualQrCode = null) => {
    setIsLoading(true);
    setIsValidCode(false);

    if (manualQrCode) {
      setRedemptionCode(manualQrCode);
      navigateWithSearch(pathname, { [REGISTER_CODE_PARAM]: manualQrCode }, true);
    }

    if (!redemptionCode && !manualQrCode) {
      setIsLoading(false);
      return;
    }

    try {
      const redemptionResult = await crud.get({
        path: `/redemptions/validate/${manualQrCode || redemptionCode}`,
        cacheControl: 'no-cache',
      });

      if (!redemptionResult?.found) {
        if (manualQrCode) {
          setErrorMessage(redemptionResult?.message ?? 'The code you entered was not found. Please try again or contact support.');
          handleTabChange(TABS.INVALID);
        }
        setIsLoading(false);
        return;
      }

      setIsValidCode(true);
      setIsBlocked(redemptionResult?.statusId === REDEMPTION_CODE_STATUS_IDS.BLOCKED);

      if (redemptionResult?.statusId === REDEMPTION_CODE_STATUS_IDS.CLAIMED) {
        if (!redemptionResult?.isAuthFreeFlow) {
          navigateWithSearch(
            ROUTE_TAKE_BACK_BAGS_DETAILS,
            { [REGISTER_CODE_PARAM]: manualQrCode || redemptionCode },
          );
          return;
        }
        try {
          await getExistingRedemption();
        } catch {
          setIsLoading(false);
          handleTabChange(TABS.SHIPPING_METHOD_GENERATION_ERROR);
          return;
        }
        try {
          await getExistingRedemption();
        } catch {
          setIsLoading(false);
          handleTabChange(TABS.SHIPPING_METHOD_GENERATION_ERROR);
          return;
        }
        try {
          await getExistingRedemption();
        } catch {
          setIsLoading(false);
          handleTabChange(TABS.SHIPPING_METHOD_GENERATION_ERROR);
          return;
        }
        setShowRewardDialog(false);
        handleTabChange(TABS.SHIPPING_REWARD);
      }

      const redemptionRequirePin = !!redemptionResult?.requirePIN;
      setRequirePin(redemptionRequirePin);

      await getAndSetStore(redemptionResult.storeId);
      setRecyclingProductType(redemptionResult?.recyclingProductType);

      setPayOnShipment({
        isPayOnShip: redemptionResult?.isPayOnShip,
        payOnShipFee: redemptionResult?.payOnShipFee,
        isPaid: redemptionResult?.isPaid,
      });

      if (manualQrCode) {
        if (redemptionResult?.isPayOnShip && userData) {
          handlePayOnShipment(redemptionResult);
          return;
        }

        if (redemptionRequirePin) {
          handleTabChange(TABS.PIN);
        } else {
          await handlePinStep();
        }
      } else if (redemptionResult?.isPayOnShip) {
        handlePayOnShipment(redemptionResult);
        return;
      }

      setIsLoading(false);
    } catch {
      setIsValidCode(false);
      if (manualQrCode) {
        setErrorMessage('An error occurred while validating your code. Please try again or contact support.');
        handleTabChange(TABS.INVALID);
      }

      setIsLoading(false);
    }
  }, [redemptionCode, query, pathname, handlePayOnShipment]);

  const stepTrackerProps = useMemo(() => (
    getStepButtonTrackerProps(
      tab,
      recyclingProductType,
      payOnShipment.isPayOnShip,
      storeConfig,
    )
  ), [tab, recyclingProductType, payOnShipment.isPayOnShip]);

  const handleSetPinValues = useCallback((values) => {
    setPinValues(values);
  }, []);

  const handleOnSelectingAddress = useCallback((address) => {
    setFromAddress(address);
    handleTabChange(TABS.SELECT_SHIPPING_METHOD);
  }, []);

  const handleBackToSelectAddress = useCallback(() => {
    setFromAddress({});
    handleTabChange(TABS.SELECT_ADDRESS);
  }, []);

  const handleNewAddress = useCallback((resetAddress = true) => {
    if (resetAddress) setFromAddress({});
    handleTabChange(TABS.ENTER_NEW_ADDRESS);
  }, []);

  const handleOnSelectShippingMethod = useCallback(async () => {
    setIsLoading(true);
    setPromoCreditAmount(shippingMethod?.promotion?.amount ?? 0);

    const { error } = await postRedemptionCode({
      id: redemptionCode,
      pin: pinValues.join(''),
      email: _.isEmpty(emailInput) ? (userData?.email || user?.email) : emailInput,
      storeId: storeConfig.id || FORDAYS_STORE_ID,
      storeUid: storeConfig.uid || 1,
      fromAddress,
      shippingProviderId: shippingMethod?.uid,
    });

    if (error) {
      setIsLoading(false);
      handleTabChange(TABS.SHIPPING_METHOD_GENERATION_ERROR);
      return;
    }

    try {
      await getExistingRedemption();
    } catch {
      // non-fatal: registration succeeded, proceed to success screen
    }

    setIsLoading(false);

    pushBagRegisteredEvent({
      bagId: redemptionCode,
      bagType: recyclingProductType,
      userData,
      courier: shippingMethod?.name,
    });

    handleTabChange(isVirtualBox ? TABS.POST_REGISTRATION_VIRTUAL_BOX : TABS.SHIPPING_REWARD);
  }, [
    redemptionCode,
    pinValues,
    storeConfig,
    fromAddress,
    shippingMethod,
    isVirtualBox,
    userData,
    user,
    emailInput,
    recyclingProductType,
    getExistingRedemption,
    handleTabChange,
  ]);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(couponData?.couponCode);
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, '1850');
  }, [couponData]);

  const handleLoginStep = useCallback(async ({
    [REGISTER_PIN_PARAM]: pin,
  }) => {
    if (firebase.auth().isSignInWithEmailLink(window.location.href)) {
      if (payOnShipment.isPayOnShip) {
        handlePayOnShipment(payOnShipment);
        return;
      }

      if (requirePin) {
        if (!pin) {
          handleGenericError();
          setIsLoading(false);
          return;
        }

        const response = await crud.post({
          path: '/redemptions/validatePinCode',
          body: {
            data: {
              id: redemptionCode,
              pin,
            },
          },
        });

        if (response.invalid) {
          handleSetPinValues([]);
          handleGenericError();
          setIsLoading(false);
          return;
        }

        handleSetPinValues(pin.split(''));

        if (isTechRecycling) {
          handleTabChange(TABS.RECYCLING_LIST);
        } else if (isVirtualBox) {
          const isValid = await validateCodeAssignedToUser();
          if (!isValid) {
            return;
          }
          handleTabChange(TABS.REUSE_BOX);
        } else {
          handleTabChange(TABS.SELECT_ADDRESS);
        }

        setIsLoading(false);
        return;
      }
    }

    if (payOnShipment.isPayOnShip) {
      handlePayOnShipment(payOnShipment);
      return;
    }

    if (requirePin && pinValues.join('') === '') {
      handleGenericError();
      setIsLoading(false);
      return;
    }

    if (isTechRecycling) {
      handleTabChange(TABS.RECYCLING_LIST);
    } else if (isVirtualBox) {
      const isValid = await validateCodeAssignedToUser();
      if (!isValid) {
        return;
      }
      handleTabChange(TABS.REUSE_BOX);
    } else {
      handleTabChange(TABS.SELECT_ADDRESS);
    }

    setIsLoading(false);
  }, [
    handleTabChange,
    handleSetPinValues,
    handlePayOnShipment,
    pinValues,
    redemptionCode,
    requirePin,
    isTechRecycling,
    isVirtualBox,
    payOnShipment.isPayOnShip,
    payOnShipment.isPaid,
    validateCodeAssignedToUser,
  ]);

  const handleWelcomeStep = useCallback(async () => {
    if (isBlocked) {
      handleTabChange(TABS.PIN);
      return;
    }

    if (!isValidCode) {
      handleTabChange(TABS.QR_CODE_FORM);
      return;
    }

    if (payOnShipment.isPayOnShip) {
      handleTabChange(TABS.SERVICE);
      return;
    }

    if (requirePin) {
      handleTabChange(TABS.PIN);
    } else {
      await handlePinStep();
    }
  }, [isBlocked, isValidCode, requirePin, payOnShipment.isPayOnShip, handlePinStep]);

  const handleQrCodeFormStep = useCallback(async (code) => {
    await validateCode(code);
  }, [validateCode]);

  const handleInvalidStep = useCallback(() => {
    handleTabChange(TABS.QR_CODE_FORM);
  }, []);

  const handleEmailFormStep = useCallback(() => {
    handleTabChange(TABS.ENTER_NEW_ADDRESS);
  }, []);

  const handleRecyclingListStep = useCallback(() => {
    if (isVirtualBox) {
      handleTabChange(TABS.BATTERY_ALERT);
    } else {
      handleTabChange(TABS.DEVICE_BATTERY_WARNING);
    }
  }, [isVirtualBox]);

  const handleReuseBoxStep = useCallback(() => {
    handleTabChange(TABS.RECYCLING_LIST);
  }, []);

  const handleBatteryAlertStep = useCallback(() => {
    handleTabChange(TABS.SELECT_ADDRESS);
  }, []);

  const handleOnBatteryWarningSubmit = useCallback(() => {
    handleTabChange(TABS.STICKERS_ADVICE);
  }, []);

  const handleOnStickersAdviceSubmit = useCallback(() => {
    handleTabChange(TABS.SELECT_ADDRESS);
  }, []);

  const handleOnQrFormSubmit = useCallback(() => {
    handleTabChange(TABS.COUPON);
  }, []);

  const getLoadingContent = useCallback(() => {
    if (
      getSearchParam(query, REGISTER_FROM_CHECKOUT_PARAM) === 'true' &&
      tab === TABS.WELCOME
    ) {
      return <ProcessingPayment isPayOnReg />;
    }

    if (
      storeConfig.isTrashie &&
      (tab === TABS.SELECT_SHIPPING_METHOD || tab === TABS.SHIPPING_REWARD)
    ) {
      return (
        <GeneratingShippingLabel
          isTechRecycling={isTechRecycling}
          isVirtualBox={isVirtualBox}
          onShowingAnimation={setShowingAnimation}
        />
      );
    }

    return <LoadingBar className="loader" />;
  }, [tab, storeConfig.isTrashie, payOnShipment.isPayOnShip, payOnShipment.isPaid, query]);

  useEffect(() => {
    if (redemptionCode === null) {
      let newRedemptionCode = null;
      const urlCode = query.get(REGISTER_CODE_PARAM);

      if (urlCode) {
        const cleanedCode = ERROR_PATTERN.test(urlCode) ? cleanCode(urlCode) : urlCode;
        const splitCode = cleanedCode.split('-')[0] ?? '';

        if (splitCode.length === CODE_LENGTH && Number(splitCode)) {
          newRedemptionCode = cleanedCode;
        } else if (cleanedCode.length === CODE_LENGTH && Number(cleanedCode)) {
          newRedemptionCode = cleanedCode;
        } else if (Number.isNaN(cleanedCode)) {
          try {
            newRedemptionCode = window.atob(cleanedCode);
          } catch (e) {
            newRedemptionCode = null;
          }
        }
      }

      setRedemptionCode(newRedemptionCode);
      if (!newRedemptionCode) {
        setIsLoading(false);
      }
    }

    if (firebase.auth().isSignInWithEmailLink(window.location.href)) {
      handleTabChange(TABS.LOGIN);
    }
  }, []);

  useEffect(() => {
    if (redemptionCode && isValidCode === null && !isLoadingFirebase) {
      validateCode();
    }
  }, [redemptionCode, isLoadingFirebase]);

  // Safety net: handle fromCheckout redirect when isSignedIn becomes true
  // after the initial handlePayOnShipment call may have missed it.
  useEffect(() => {
    const searchParams = new URLSearchParams(search);
    const fromCheckout = getSearchParam(searchParams, REGISTER_FROM_CHECKOUT_PARAM) === 'true';

    if (
      !fromCheckout
      || isLoadingFirebase
      || !payOnShipment.isPayOnShip
      || tab !== TABS.WELCOME
      || paymentStatusInFlightRef.current
    ) {
      return undefined;
    }

    // Auth never restored — show error with reload option after timeout
    if (!isSignedIn) {
      const timeout = setTimeout(() => {
        setMessageContent({
          image: IconCloudy,
          title: 'WE\'RE HAVING TROUBLE REGISTERING THE BAG AFTER CHECKOUT',
          cta: {
            text: 'TRY AGAIN',
            onClick: () => window.location.reload(),
          },
        });
        handleTabChange(TABS.MESSAGE);
        setIsLoading(false);
      }, 10000);
      return () => clearTimeout(timeout);
    }

    let cancelled = false;

    const handleFromCheckout = async () => {
      setIsLoading(true);
      await validatePaymentStatus(true);
      if (!cancelled) {
        setIsLoading(false);
      }
    };
    handleFromCheckout();

    return () => { cancelled = true; };
  }, [
    isSignedIn, isLoadingFirebase, payOnShipment.isPayOnShip,
    tab, search, validatePaymentStatus,
  ]);

  useEffect(() => {
    let newStepIndex = steps.findIndex(item => item.tabs.includes(tab));

    if (newStepIndex < 0) {
      newStepIndex = 0;
    }

    setStep({
      ...steps[newStepIndex],
      index: newStepIndex,
    });
  }, [tab, steps]);

  if (isLoading) {
    return (
      <div className="RegisterView">
        <div className="RegisterView__contentContainer">
          {getLoadingContent()}
        </div>
      </div>
    );
  }

  return (
    <div className="RegisterView">
      <div
        className={classNames('RegisterView__container', {
          rainbowBg: (storeConfig.isTrashie || !isValidCode)
            && TABS.WELCOME === tab && !payOnShipment.isPayOnShip,
          rainbowAlternative: isVirtualBox && TABS.WELCOME === tab,
        })}
        style={{ background: step.index === 0 ? storeConfig?.registerTbbFlow?.landingPageStep?.bgColor : '' }}
      >
        {(step.header || (tab === TABS.SERVICE && payOnShipment.isPayOnShip))
          && !showingAnimation && (
          <div className="RegisterView__header">
            <div className="RegisterView__header--content">
              <img
                src={getHeaderLogo(storeConfig)}
                alt="recycle bag"
                style={storeConfig?.registerTbbFlow?.headerLogoProperties}
              />
              <span className="RegisterView__header--content-text">
                {isVirtualBox
                  ? 'TECH TAKE BACK BOX'
                  : redemptionCode && `${isTechRecycling ? 'BOX ID' : 'BAG ID'}: *******${redemptionCode.slice(-6)}`}
              </span>
            </div>
          </div>
        )}
        {step.stepper && !showingAnimation && (
          <div className="RegisterView__stepperContainer">
            <Stepper
              activeStep={
                tab === TABS.POST_REGISTRATION_VIRTUAL_BOX ? steps.length - 1 : step.index - 1
              }
              alternativeLabel
              color={!storeConfig?.isTrashie ? storeConfig?.registerTbbFlow?.nextButton?.bgColor : ''}
            >
              {steps.map(({ label, labelAuthFreeFlow, stepper }) => (
                stepper ? (
                  <Step key={label}>
                    <StepLabel>
                      {(storeConfig?.registerTbbFlow?.isAuthFreeFlow && labelAuthFreeFlow) || label}
                    </StepLabel>
                  </Step>
                ) : null
              ))}
            </Stepper>
          </div>
        )}
        <div className="RegisterView__contentContainer">
          <Tabs
            activeKey={tab}
            transition={false}
            mountOnEnter
            unmountOnExit
          >
            <Tab
              eventKey={TABS.WELCOME}
              {...TABS_PROPS}
            >
              {(() => {
                if (payOnShipment.isPayOnShip) {
                  return (
                    <WelcomePayOnShip
                      handleOnClick={handleWelcomeStep}
                      storeConfig={storeConfig}
                      stepTrackerProps={stepTrackerProps}
                      isValidCode={isValidCode}
                    />
                  );
                }
                if (isVirtualBox) {
                  return (
                    <WelcomeVirtualBox
                      handleOnClick={handleWelcomeStep}
                      storeConfig={storeConfig}
                      stepTrackerProps={stepTrackerProps}
                      isValidCode={isValidCode}
                    />
                  );
                }
                return (
                  <Welcome
                    handleOnClick={handleWelcomeStep}
                    recyclingProductType={recyclingProductType}
                    storeConfig={storeConfig}
                    stepTrackerProps={stepTrackerProps}
                    isValidCode={isValidCode}
                  />
                );
              })()}
            </Tab>
            <Tab
              eventKey={TABS.QR_CODE_FORM}
              {...TABS_PROPS}
            >
              <QrCodeForm
                onCancel={handleContactSupport}
                onClick={handleQrCodeFormStep}
              />
            </Tab>
            <Tab
              eventKey={TABS.INVALID}
              {...TABS_PROPS}
            >
              <Invalid
                message={errorMessage}
                onClick={handleInvalidStep}
                onCancel={handleContactSupport}
                code={redemptionCode}
                storeConfig={storeConfig}
                recyclingProductType={recyclingProductType}
              />
            </Tab>
            <Tab
              eventKey={TABS.SERVICE}
              {...TABS_PROPS}
            >
              <Service
                code={redemptionCode}
                creditAmt={creditAmt}
                isPayOnShip={payOnShipment.isPayOnShip}
                storeConfig={storeConfig}
                onSubmit={handleServiceStep}
                stepTrackerProps={stepTrackerProps}
                isLoadingPayOnShipConfig={isLoadingPayOnShipConfig}
                payOnShipConfig={payOnShipConfig}
              />
            </Tab>
            <Tab
              eventKey={TABS.PAYMENT}
              {...TABS_PROPS}
            >
              <Payment
                code={redemptionCode}
                payOnShipFee={payOnShipment.payOnShipFee}
                storeConfig={storeConfig}
                isPayOnShip={payOnShipment.isPayOnShip}
                isLoadingPayOnShipConfig={isLoadingPayOnShipConfig}
                payOnShipConfig={payOnShipConfig}
              />
            </Tab>
            <Tab
              eventKey={TABS.PIN}
              {...TABS_PROPS}
            >
              <Pin
                storeConfig={storeConfig}
                onSubmit={handlePinStep}
                onCancel={handleContactSupport}
                onShowingAnimation={setShowingAnimation}
                registerCode={redemptionCode}
                pinValues={pinValues}
                setPinValues={handleSetPinValues}
                recyclingProductType={recyclingProductType}
                stepTrackerProps={stepTrackerProps}
                isPaymentSuccessful={payOnShipment.isPayOnShip && payOnShipment.isPaid}
                isBlocked={isBlocked}
              />
            </Tab>
            <Tab
              eventKey={TABS.LOGIN}
              {...TABS_PROPS}
            >
              <Login
                storeConfig={storeConfig}
                emailLinkParams={{ [REGISTER_PIN_PARAM]: pinValues.join('') }}
                onSubmit={handleLoginStep}
                onError={handleGenericError}
                onEmailLinkSentAuth={() => handleLoginStep({ [REGISTER_PIN_PARAM]: pinValues.join('') })}
              />
            </Tab>
            <Tab
              eventKey={TABS.RECYCLING_LIST}
              {...TABS_PROPS}
            >
              <RecyclingList
                isVirtualBox={isVirtualBox}
                stepTrackerProps={stepTrackerProps}
                onSubmit={handleRecyclingListStep}
              />
            </Tab>
            <Tab
              eventKey={TABS.DEVICE_BATTERY_WARNING}
              {...TABS_PROPS}
            >
              <DeviceBatteryWarning
                stepTrackerProps={stepTrackerProps}
                onCancel={handleContactSupport}
                onSubmit={handleOnBatteryWarningSubmit}
              />
            </Tab>
            <Tab
              eventKey={TABS.STICKERS_ADVICE}
              {...TABS_PROPS}
            >
              <StickersAdvice
                onSubmit={handleOnStickersAdviceSubmit}
                stepTrackerProps={stepTrackerProps}
              />
            </Tab>
            <Tab
              eventKey={TABS.REUSE_BOX}
              {...TABS_PROPS}
            >
              <ReuseBox
                onSubmit={handleReuseBoxStep}
                stepTrackerProps={stepTrackerProps}
              />
            </Tab>
            <Tab
              eventKey={TABS.BATTERY_ALERT}
              {...TABS_PROPS}
            >
              <BatteryAlert
                onSubmit={handleBatteryAlertStep}
                stepTrackerProps={stepTrackerProps}
              />
            </Tab>
            <Tab
              eventKey={TABS.EMAIL_FORM}
              {...TABS_PROPS}
            >
              <EmailForm
                storeConfig={storeConfig}
                email={emailInput}
                recyclingProductType={recyclingProductType}
                onEmailChange={setEmailInput}
                onSubmit={handleEmailFormStep}
                stepTrackerProps={stepTrackerProps}
              />
            </Tab>
            <Tab
              eventKey={TABS.SELECT_ADDRESS}
              {...TABS_PROPS}
            >
              <SelectAddressView
                addressSelected={fromAddress}
                stepTrackerProps={stepTrackerProps}
                onSubmit={handleOnSelectingAddress}
                onEmptyAddresses={() => handleTabChange(TABS.ENTER_NEW_ADDRESS)}
                onNewAddress={handleNewAddress}
                storeConfig={storeConfig}
              />
            </Tab>
            <Tab
              eventKey={TABS.ENTER_NEW_ADDRESS}
              {...TABS_PROPS}
            >
              <EnterNewAddressView
                addressSelected={fromAddress}
                userData={userData}
                onBackToSelectAddress={handleBackToSelectAddress}
                setFromAddress={handleOnSelectingAddress}
                storeConfig={storeConfig}
                recyclingProductType={recyclingProductType}
                stepTrackerProps={stepTrackerProps}
              />
            </Tab>
            <Tab
              eventKey={TABS.SELECT_SHIPPING_METHOD}
              {...TABS_PROPS}
            >
              <SelectShippingProviderView
                setShippingMethod={setShippingMethod}
                onSubmitShippingMethod={handleOnSelectShippingMethod}
                onReturnToAddress={returnToAddressStep}
                storeConfig={storeConfig}
                selectedShippingMethodIndex={selectedShippingMethodIndex}
                setSelectedShippingMethodIndex={setSelectedShippingMethodIndex}
                address={fromAddress}
                onError={handleGenericError}
                recyclingProductType={recyclingProductType}
                stepTrackerProps={stepTrackerProps}
              />
            </Tab>
            <Tab
              eventKey={TABS.SHIPPING_METHOD_GENERATION_ERROR}
              {...TABS_PROPS}
            >
              <ShippingLabelGenerationError
                onRetry={() => handleTabChange(TABS.SELECT_SHIPPING_METHOD)}
                onGetSupport={handleContactSupport}
                storeConfig={storeConfig}
              />
            </Tab>
            <Tab
              eventKey={TABS.POST_REGISTRATION_VIRTUAL_BOX}
              {...TABS_PROPS}
            >
              <PostRegistrationVirtualBox
                storeConfig={storeConfig}
                shippingMethod={shippingMethod}
                address={fromAddress}
                onClick={() => handleTabChange(TABS.SHIPPING_DETAILS)}
                stepTrackerProps={stepTrackerProps}
                showSuccessAnimation={showRewardDialog}
                creditAmt={creditAmt}
              />
            </Tab>
            <Tab
              eventKey={TABS.QR}
              {...TABS_PROPS}
            >
              <QrView
                storeConfig={storeConfig}
                qrCodeImgUrl={qrCodeImgUrl}
                printLabelUrl={printLabelUrl}
                onQrFormButtonClick={handleOnQrFormSubmit}
                isAQrExpired={isAQrExpired}
                expirationLabelDate={expirationLabelDate}
                stepTrackerProps={stepTrackerProps}
              />
            </Tab>
            <Tab
              eventKey={TABS.COUPON}
              {...TABS_PROPS}
            >
              <TbbRewardPage
                creditAmt={creditAmt}
                fromAddress={fromAddress}
                storeConfig={storeConfig}
                couponData={couponData}
                copied={copied}
                handleCopy={handleCopy}
                emailInput={emailInput}
                isNewUser={isNewUser}
                stepTrackerProps={stepTrackerProps}
              />
            </Tab>
            <Tab
              eventKey={TABS.MESSAGE}
              {...TABS_PROPS}
            >
              <MessageContent {...messageContent} />
            </Tab>
            <Tab
              eventKey={TABS.SHIPPING_REWARD}
              {...TABS_PROPS}
            >
              {(() => {
                if (payOnShipment.isPayOnShip) {
                  return (
                    <PostRegistration
                      storeConfig={storeConfig}
                      onClick={() => handleTabChange(TABS.SHIPPING_DETAILS)}
                      shippingMethod={shippingMethod}
                      address={fromAddress}
                      stepTrackerProps={stepTrackerProps}
                      redemptionCode={redemptionCode}
                      showSuccessAnimation={showRewardDialog}
                      isTechRecycling={isTechRecycling}
                      isPayOnShip={payOnShipment.isPayOnShip}
                      isLoadingPayOnShipConfig={isLoadingPayOnShipConfig}
                      payOnShipConfig={payOnShipConfig}
                    />
                  );
                }
                if (isVirtualBox) {
                  return (
                    <PostRegistrationVirtualBox
                      storeConfig={storeConfig}
                      onClick={() => handleTabChange(TABS.SHIPPING_DETAILS)}
                      shippingMethod={shippingMethod}
                      address={fromAddress}
                      stepTrackerProps={stepTrackerProps}
                      redemptionCode={redemptionCode}
                      showSuccessAnimation={showRewardDialog}
                      isTechRecycling
                      creditAmt={creditAmt}
                      printLabelUrl={printLabelUrl}
                      qrCodeImgUrl={qrCodeImgUrl}
                    />
                  );
                }
                return (
                  <ShippingAndReward
                    storeConfig={storeConfig}
                    onClick={() => handleTabChange(TABS.SHIPPING_DETAILS)}
                    shippingMethod={shippingMethod}
                    creditAmt={creditAmt}
                    promoCreditAmount={promoCreditAmount}
                    couponData={couponData}
                    address={fromAddress}
                    showDialog={showRewardDialog}
                    redemptionCode={redemptionCode}
                    stepTrackerProps={stepTrackerProps}
                    isTechRecycling={isTechRecycling}
                  />
                );
              })()}
            </Tab>
            <Tab
              eventKey={TABS.SHIPPING_DETAILS}
              {...TABS_PROPS}
            >
              <ShippingDetails
                code={redemptionCode}
                shippingMethod={shippingMethod}
                expirationLabelDate={expirationLabelDate}
                qrCodeImgUrl={qrCodeImgUrl}
                printLabelUrl={printLabelUrl}
                address={fromAddress}
                isAuthFreeFlow={storeConfig?.registerTbbFlow?.isAuthFreeFlow}
                recyclingProductType={recyclingProductType}
                storeConfig={storeConfig}
              />
            </Tab>
          </Tabs>
        </div>
        {![
          TABS.WELCOME,
          TABS.SHIPPING_DETAILS,
          TABS.LOGIN,
          TABS.SERVICE,
          TABS.SHIPPING_REWARD,
        ].includes(tab) && (
          <div className="RegisterView__footer">
            <span>Powered by</span>
            <a href="https://www.trashie.io">Trashie</a>
          </div>
        )}
      </div>
    </div>
  );
};

export default RedemptionLandingPage;
